import { NavLink, useLocation } from 'react-router-dom';
import type { Language } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import {
  IconDashboard,
  IconChat,
  IconResults,
  IconStar,
  IconSettings,
  IconLogOut,
} from '../icons';

interface SidebarProps {
  lang: Language;
  onLogout: () => void;
  userName: string;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ size?: number }>;
  label: string;
}

const S = {
  sidebar: {
    width: 220,
    minHeight: '100dvh',
    backgroundColor: 'var(--bg-surface)',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column' as const,
    flexShrink: 0,
    padding: '6px 14px 14px',
  },
  logo: {
    padding: '14px 6px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logoMark: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background:
      'linear-gradient(135deg, var(--accent) 0%, var(--accent) 50%, transparent 50%), oklch(95% 0.005 var(--hue))',
    color: 'oklch(20% 0.012 var(--hue))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: '-0.04em',
    flexShrink: 0,
  },
  logoText: {
    fontWeight: 600,
    fontSize: 15,
    color: 'var(--text-primary)',
    letterSpacing: '-0.01em',
    lineHeight: 1.1,
  },
  logoTag: {
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.12em',
    marginTop: 1,
  },
  sectionLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.12em',
    color: 'var(--text-muted)',
    padding: '14px 6px 6px',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 'var(--r-md)',
    textDecoration: 'none',
    color: 'var(--text-secondary)',
    fontSize: 13.5,
    fontWeight: 450,
    transition: 'background 0.1s, color 0.1s',
    position: 'relative' as const,
  },
  navItemActive: {
    backgroundColor: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
  },
  activeBar: {
    position: 'absolute' as const,
    left: -14,
    top: 8,
    bottom: 8,
    width: 2,
    background: 'var(--accent)',
    borderRadius: 2,
  },
  footer: {
    marginTop: 8,
    padding: '8px 10px',
    borderRadius: 'var(--r-md)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: 'var(--accent-subtle)',
    color: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  userName: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-primary)',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.12s',
  },
};

export function Sidebar({ lang, onLogout, userName }: SidebarProps) {
  const nav = I18N[lang].nav;
  const location = useLocation();

  const items: NavItem[] = [
    { to: '/dashboard', icon: IconDashboard, label: nav.dashboard },
    { to: '/chat', icon: IconChat, label: nav.newSearch },
    { to: '/results', icon: IconResults, label: nav.results },
    { to: '/favorites', icon: IconStar, label: nav.favorites },
    { to: '/settings', icon: IconSettings, label: nav.settings },
  ];

  const initials = userName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const workspaceItems = items.slice(0, 4);
  const accountItems = items.slice(4);

  const renderItem = ({ to, icon: Icon, label }: NavItem) => {
    const isActive =
      to === '/dashboard'
        ? location.pathname === '/' || location.pathname.startsWith('/dashboard')
        : location.pathname.startsWith(to);

    return (
      <NavLink
        key={to}
        to={to}
        style={{
          ...S.navItem,
          ...(isActive ? S.navItemActive : {}),
        }}
      >
        {isActive && <span style={S.activeBar} aria-hidden="true" />}
        <Icon size={16} />
        {label}
      </NavLink>
    );
  };

  return (
    <nav style={S.sidebar} aria-label="Main navigation">
      {/* Logo */}
      <div style={S.logo}>
        <div style={S.logoMark}>S</div>
        <div>
          <div style={S.logoText}>Sourcr</div>
          <div style={S.logoTag}>v1.0 · MVP</div>
        </div>
      </div>

      {/* Workspace nav */}
      <div style={S.sectionLabel}>{lang === 'uk' ? 'Простір' : 'Workspace'}</div>
      <div style={S.nav}>
        {workspaceItems.map(renderItem)}

        {/* Account section */}
        <div style={S.sectionLabel}>{lang === 'uk' ? 'Акаунт' : 'Account'}</div>
        {accountItems.map(renderItem)}
      </div>

      {/* Footer */}
      <div style={S.footer}>
        <div style={S.avatar}>{initials}</div>
        <span style={S.userName}>{userName}</span>
        <button
          type="button"
          onClick={onLogout}
          style={S.logoutBtn}
          title="Sign out"
          aria-label="Sign out"
        >
          <IconLogOut size={15} />
        </button>
      </div>
    </nav>
  );
}
